// friendlyError domains for the reserved-address refusal (SEC-INVITE, Jason
// 5 Oct 2026): invite-user and reset-user-password refuse any `.invalid`
// address except a staff login name (`name@<business>.staff.invalid`);
// approve-tenant-signup refuses every `.invalid` address, staff login names
// included (SEC-PROV, 5 Oct 2026 evening). Every refusal carries the server's
// RESERVED_ADDRESS_MARKER (supabase/functions/_shared/reservedAddress.ts);
// reservedAddressDomain.test.ts reads that file and pins the two together.
import { ValidationError } from '@/lib/errorReporter'

const MARKER = /is a reserved \.invalid address/

// SEC-PROV3 (Jason, 5 Oct 2026 ~15:45): invite, signup approval and the
// provision-* functions also refuse an address with a hidden or look-alike
// character in it (zero-width, bidi, control, non-ASCII), or without exactly
// one @ — 400 `invalid_email`, carrying the server's HIDDEN_CHARACTER_MARKER /
// AT_SIGN_MARKER (supabase/functions/_shared/plainTextEmail.ts). The server
// sentence marks each character as [U+XXXX]; these name the next step.
const HIDDEN_CHARACTER = /has a hidden or look-alike character in it/
// SEC-PROV6: the login-name form ("No mailbox" ticked) of the same markers.
const AT_SIGN = /is not (?:an email address|a login name): it needs exactly one @/
// SEC-PROV5 (Jason, 6 Oct 2026 ~15:35): an accented / other-script letter, an
// address over 254 characters and a punycode (xn--) domain are refused too —
// PLAIN_LETTERS_MARKER / TOO_LONG_MARKER / INTERNATIONAL_DOMAIN_MARKER. The
// letter is not "typed again by hand" (it was typed on purpose, so that loops).
const PLAIN_LETTERS = /must use plain letters \(a\u2013z\)/
const TOO_LONG = /is too long for (?:an email address|a login name)/
const INTERNATIONAL_DOMAIN = /is at an internationalised domain, and those can't be used here/
// SEC-PROV6 (Jason, 7 Oct 2026 ~10:25): a space inside the address, and a
// compatibility letter (fullwidth, a ligature, the Kelvin sign) —
// SPACE_MARKER / PLAIN_AZ_MARKER. Neither marker names the kind.
const SPACE = /can't contain a space/
const PLAIN_AZ = /must use plain letters a\u2013z only/

/** What each plain-text row tells the person to do next. */
interface PlainTextSteps {
  /** hidden character / not one @: delete it and type it again by hand */
  retype: string
  /** an accented letter: completes "Email addresses here must use plain letters (a–z) …" */
  letters: string
  /** a fullwidth letter, a ligature, the Kelvin sign: completes "… plain letters a–z only …" */
  plainAz: string
  /** a space inside: completes "An email address can't contain a space …" */
  space: string
  /** too long / punycode domain: retyping cannot help, a different address can */
  other: string
}

/** The words each kind is named by: an email address, or (invite with "No
 *  mailbox" ticked) a staff login name. */
const NOUN = {
  email: { a: 'An email address', aLower: 'an email address', that: 'That email address', plural: 'Email addresses', short: 'address', aShort: 'an address' },
  login: { a: 'A login name',     aLower: 'a login name',     that: 'That login name',    plural: 'Login names',     short: 'login name', aShort: 'a login name' },
} as const

/** The plain-text rows, worded for who the address belongs to, and for what
 *  the field holds. */
function plainTextRows(whose: string, steps: PlainTextSteps, kind: keyof typeof NOUN = 'email'): [RegExp, string][] {
  const n = NOUN[kind]
  const reach = kind === 'login' ? `${whose} could not sign in with the name they were given` : `nothing sent to it would reach ${whose}`
  return [
    [HIDDEN_CHARACTER, `${n.that} has a hidden or look-alike character in it, usually picked up when ${n.aShort} is copied ` +
                       `from a document, a chat or a web page. It is not the ${n.short} it looks like, so ${reach}. ${steps.retype}`],
    [AT_SIGN,          `That is not ${n.aLower}: it needs exactly one @. ${steps.retype}`],
    [SPACE,            `${n.a} can't contain a space ${steps.space}`],
    [PLAIN_AZ,         `${n.plural} here must use plain letters a\u2013z only ${steps.plainAz}`],
    [PLAIN_LETTERS,    `${n.plural} here must use plain letters (a\u2013z) ${steps.letters}`],
    [TOO_LONG,         `${n.that} is too long: ${n.aShort} can have at most 254 characters. ${steps.other}`],
    [INTERNATIONAL_DOMAIN,
                       `${n.that} is at an internationalised domain (a part of it after the @ starts with xn--), and those can't be ` +
                       `used here: one can be made to look exactly like a different, ordinary domain. ${steps.other}`],
  ]
}
const RETYPE = 'Delete it and type the address again by hand. Nothing was created.'
const TYPED_HERE: Omit<PlainTextSteps, 'other'> = {
  retype:  RETYPE,
  letters: '\u2014 type it without accents. Nothing was created.',
  plainAz: '\u2014 type it again. Nothing was created.',
  space:   '\u2014 check it and type it again. Nothing was created.',
}

/** invite-user — every invite form (members pages, Users, agent + supplier config). */
export const INVITE_ADDRESS_DOMAIN: [RegExp, string][] = [
  [MARKER, 'That address ends in .invalid — a made-up one, like a worker\'s floor-device sign-in, not a person\'s mailbox — so it can\'t be invited. ' +
           'Use the person\'s own email address, or a staff login name (name@<business>.staff.invalid). Nothing was created.'],
  ...plainTextRows('the person', { ...TYPED_HERE, other: 'Use a different email address. Nothing was created.' }),
]

/** invite-user with "No mailbox" ticked — the field is a LOGIN NAME, so every
 *  sentence names one (SEC-PROV6). The server's own login-name-form refusal
 *  (NO_MAILBOX_LOGIN_FORM_REFUSAL) matches no row here and is shown as sent. */
export const LOGIN_NAME_ADDRESS_DOMAIN: [RegExp, string][] = [
  [MARKER, 'That login name ends in .invalid but is not a staff login name (name@<business>.staff.invalid), so it can\'t be made. ' +
           'Type it in that form, or untick "No mailbox" to invite a real email address. Nothing was created.'],
  ...plainTextRows('the person', {
    ...TYPED_HERE,
    retype: 'Delete it and type the login name again by hand. Nothing was created.',
    other:  'Use a different login name. Nothing was created.',
  }, 'login'),
]

/** The invite dialogs' domain: the login-name wording while "No mailbox" is ticked. */
export function inviteAddressDomain(noMailbox: boolean | undefined): [RegExp, string][] {
  return noMailbox ? LOGIN_NAME_ADDRESS_DOMAIN : INVITE_ADDRESS_DOMAIN
}

/** invite-user's BigFinance-only mode (BigFinanceUsers' Invite by email) —
 *  refuses staff login names too: an outside accountant has a mailbox. */
export const APP_INVITE_ADDRESS_DOMAIN: [RegExp, string][] = [
  [MARKER, 'That address ends in .invalid, a made-up one and not a person\'s mailbox, so it can\'t be invited. ' +
           'Use the accountant\'s own email address. Nothing was created.'],
  ...plainTextRows('the accountant', { ...TYPED_HERE, other: 'Use a different email address for the accountant. Nothing was created.' }),
]

/** reset-user-password — Reset password, and Resend invite on a pending member. */
export const RESET_ADDRESS_DOMAIN: [RegExp, string][] = [
  [MARKER, 'This login\'s address ends in .invalid — a made-up one, like a worker\'s floor-device sign-in — so it is not reset from here. ' +
           'No reset email or link was made.'],
]

/** approve-tenant-signup — the platform admin's signup review card. Refuses
 *  every `.invalid` address, a staff login name included (a farmer signing up
 *  has a mailbox), so the sentence names the ending, not the device shape. */
export const SIGNUP_ADDRESS_DOMAIN: [RegExp, string][] = [
  [MARKER, 'This signup\'s admin email ends in .invalid, a made-up address nothing can be delivered to, so it can\'t be approved. ' +
           'Reject the request and ask the farmer to sign up again with their own email address. Nothing was created.'],
  ...plainTextRows('the farmer', {
    retype:  'Reject the request and ask the farmer to sign up again, typing their email address by hand. Nothing was created.',
    letters: '\u2014 this signup\'s address is not, so it can\'t be approved. Reject the request and ask the farmer to sign up again, ' +
             'typing their email address without accents. Nothing was created.',
    plainAz: '\u2014 this signup\'s address is not, so it can\'t be approved. Reject the request and ask the farmer to sign up again, ' +
             'typing their email address in plain letters. Nothing was created.',
    space:   '\u2014 this signup\'s address has one, so it can\'t be approved. Reject the request and ask the farmer to sign up again, ' +
             'typing their email address without spaces. Nothing was created.',
    other:   'Reject the request and ask the farmer to sign up again with a different email address. Nothing was created.',
  }),
]

/** request-tenant-signup — the public sign-up form (SignupTenant), where the
 *  person typing is the one the address belongs to (SEC-PROV5). Nothing is
 *  written for a refused address: no request, no rate-limit row. */
export const PUBLIC_SIGNUP_ADDRESS_DOMAIN: [RegExp, string][] = plainTextRows('you', {
  retype:  'Delete it and type your email address again by hand. Your request was not sent.',
  letters: '\u2014 type it without accents. Your request was not sent.',
  plainAz: '\u2014 type it again. Your request was not sent.',
  space:   '\u2014 check it and type it again. Your request was not sent.',
  other:   'Please use a different email address. Your request was not sent.',
})

/** The platform admin's New tenant page (ProvisionTenant) — the provision-*
 *  functions refuse a device address, and every staff login name for a NEW
 *  tenant (SEC-PROV, Jason 5 Oct 2026 evening), before anything is created.
 *  The second pattern is the server's STAFF_LOGIN_PROVISION_MARKER. */
export const PROVISION_ADDRESS_DOMAIN: [RegExp, string][] = [
  [MARKER, 'That admin email ends in .invalid, a made-up address like a worker\'s floor-device sign-in, so nothing can be delivered to it. ' +
           'Give the first admin\'s own email address. Nothing was created.'],
  [/is a staff login name, not a mailbox/,
           'That admin email is a staff login name (name@<business>.staff.invalid). A business\'s own admin makes those for its staff once it exists, ' +
           'and nothing can be delivered to one, so it can\'t be a new tenant\'s first admin. Give the first admin\'s own email address. Nothing was created.'],
  ...plainTextRows('the first admin', { ...TYPED_HERE, other: 'Use a different email address for the first admin. Nothing was created.' }),
]

/** The exporter's buyer card (ExporterReceivers' Send invite) —
 *  provision-buyer-tenant's `buyer` flow (Jason, 15 Oct 2026). A staff login
 *  name passes there only when it already runs the buyer business it names
 *  (a reused buyer tenant), so the sentence says so. */
export const BUYER_ADDRESS_DOMAIN: [RegExp, string][] = [
  [MARKER, 'That buyer email ends in .invalid, a made-up address like a worker\'s floor-device sign-in, so nothing can be delivered to it. ' +
           'Use the buyer contact\'s own email address. Nothing was created.'],
  [/is a staff login name, not a mailbox/,
           'That buyer email is a staff login name (name@<business>.staff.invalid), and nothing can be delivered to one. ' +
           'It can be used for a buyer only when it is already the admin login of the buyer business it names. ' +
           'Use the buyer contact\'s own email address. Nothing was created.'],
  ...plainTextRows('the buyer', { ...TYPED_HERE, other: 'Use a different email address for the buyer. Nothing was created.' }),
]

/**
 * `err` with the domain's sentence swapped in when it IS this refusal — as a
 * ValidationError, because a by-design refusal is not a fault to report — and
 * `err` untouched otherwise, so wiring it into a throw site changes nothing
 * else that screen shows.
 *
 * ⚠️ Reads the MESSAGE whatever the error's class. friendlyError returns a
 * ValidationError's own text before it looks at a domain, and the Users page
 * hands this a ValidationError — routed through friendlyError, its Send invite
 * and Reset password showed the server's raw sentence.
 */
export function withAddressRefusal(err: unknown, domain: [RegExp, string][]): unknown {
  const raw = typeof err === 'string' ? err : (err as { message?: unknown } | null)?.message
  const hit = typeof raw === 'string' ? domain.find(([re]) => re.test(raw)) : undefined
  return hit ? new ValidationError(hit[1]) : err
}
